import { LOCAL } from "@mn/shared";
import { useEffect, useRef } from "react";
import type * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useBoardUI } from "../store";

export function HeadingBody({ id, ymap, text }: { id: string; ymap: Y.Map<unknown>; text: string }) {
  const { handle, canEdit } = useBoard();
  const editing = useBoardUI((s) => s.editingId === id);
  const setEditing = useBoardUI((s) => s.setEditing);
  const ref = useRef<HTMLTextAreaElement>(null);
  const t = useT();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text, editing]);
  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);
  const commit = (v: string) => handle.doc.transact(() => ymap.set("text", v), LOCAL);
  if (!editing)
    return (
      <div className="display min-h-[36px] whitespace-pre-wrap break-words px-2 py-1 text-[32px] leading-[1.1]">
        {text || <span className="text-dim">{t.headingPlaceholder}</span>}
      </div>
    );
  return (
    <textarea
      ref={ref}
      value={text}
      readOnly={!canEdit}
      onChange={(e) => commit(e.target.value)}
      onBlur={() => setEditing(null)}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Escape") setEditing(null);
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          setEditing(null);
        }
      }}
      rows={1}
      className="display block w-full resize-none overflow-hidden bg-transparent px-2 py-1 text-[32px] leading-[1.1] outline-none"
      placeholder={t.headingPlaceholder}
    />
  );
}
